import * as React from "react";

import { cn } from "@/nucleo/utilitarios";

const FileInput = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, onChange, disabled, ...props }, ref) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const [fileName, setFileName] = React.useState<string>("");

    React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement, []);

    /**
     * Lê o nome a mostrar a partir do que o input REALMENTE tem agora.
     *
     * Antes daqui existia um `MutationObserver` com `attributeFilter: ["value"]`,
     * para perceber quando o pai limpasse o campo. Ele nunca disparava: o jeito
     * de limpar um `<input type="file">` é `evento.target.value = ''`, que mexe
     * na PROPRIEDADE, e atributo só muda por `setAttribute`. O nome do arquivo
     * ficava na tela como se ele tivesse sido anexado.
     */
    const sincronizarNome = React.useCallback((el: HTMLInputElement | null) => {
      const arquivos = el?.files;
      if (!arquivos || arquivos.length === 0) {
        setFileName("");
        return;
      }
      setFileName(
        arquivos.length === 1
          ? (arquivos[0]?.name ?? "")
          : `${arquivos.length} arquivos selecionados`,
      );
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // O consumidor vem primeiro porque ele pode limpar o campo dentro do
      // próprio `onChange` — é o que a etapa de documentos do portal público faz,
      // já que o arquivo não pode ser guardado enquanto não houver endpoint. Ler
      // o elemento DEPOIS mantém o nome exibido fiel ao conteúdo do input.
      onChange?.(e);
      sincronizarNome(innerRef.current);
    };

    return (
      <div
        className={cn(
          "flex h-11 w-full items-center gap-3 rounded-xl border border-input bg-background px-3 py-1.5 text-sm ring-offset-background transition-colors focus-within:outline-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
      >
        <input
          {...props}
          type="file"
          ref={innerRef}
          disabled={disabled}
          onChange={handleChange}
          className="sr-only"
        />
        <button
          type="button"
          onClick={() => innerRef.current?.click()}
          disabled={disabled}
          className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-input bg-muted px-3 text-xs font-medium text-foreground hover:bg-muted/80 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Selecionar arquivo
        </button>
        <span className={cn("truncate text-sm", fileName ? "text-foreground" : "text-muted-foreground")}>
          {fileName || "Nenhum arquivo selecionado"}
        </span>
      </div>
    );
  },
);
FileInput.displayName = "FileInput";

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, ...props }, ref) => {
    if (type === "file") {
      return <FileInput ref={ref} className={className} {...props} />;
    }
    return (
      <input
        type={type}
        className={cn(
          "flex h-11 w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground/80 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
